// src/components/modals/EditClientModal.tsx
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '@/components/common/Modal';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { useClientStore } from '@/store/clientStore';
import { Client } from '@/types';

// Match backend validation rules
const clientSchema = z.object({
  name: z.string().trim().min(1, 'Client name is required'),
  email: z.string().email('Invalid email address'),
  phone: z.string().regex(
    /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
    'Invalid phone number format'
  ),
  address: z.string().trim().min(1, 'Address is required'),
  gst: z.string().regex(
    /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
    'Invalid GST number format (e.g., 24AAHFG2234H1ZH)'
  ).optional().or(z.literal('')),
});

type ClientFormData = z.infer<typeof clientSchema>;

interface EditClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client;
}

export const EditClientModal: React.FC<EditClientModalProps> = ({ isOpen, onClose, client }) => {
  const { updateClient } = useClientStore();
  
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<ClientFormData>({
    resolver: zodResolver(clientSchema),
    defaultValues: {
      name: client.name,
      email: client.email || '',
      phone: client.phone || '',
      address: client.address || '',
      gst: client.gst || '',
    }
  });

  const onSubmit = async (data: ClientFormData) => {
    try {
      // Clean up data before sending
      const cleanedData = {
        ...data,
        gst: data.gst?.trim() || undefined, // Send undefined if empty
      };
      
      await updateClient(client._id || client.id, cleanedData);
      onClose();
    } catch (error: any) {
      // Error is already handled in the store
      console.error('Failed to update client:', error);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Client">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label="Client Name"
          placeholder="Goyal Traders"
          {...register('name')}
          error={errors.name?.message}
          required
        />

        <Input
          label="Email Address"
          type="email"
          placeholder="goyaltraders@gmail.com"
          {...register('email')}
          error={errors.email?.message}
          required
        />

        <Input
          label="Phone Number"
          placeholder="9876543210 or +91-987-654-3210"
          {...register('phone')}
          error={errors.phone?.message}
          required
        />

        <Input
          label="Address"
          placeholder="B-11 Krishna Estate, Naroi, Ahmedabad"
          {...register('address')}
          error={errors.address?.message}
          required
        />

        <Input
          label="GST Number"
          placeholder="24AAHFG2234H1ZH (Optional)"
          {...register('gst')}
          error={errors.gst?.message}
          onChange={(e) => {
            // Convert to uppercase for GST
            e.target.value = e.target.value.toUpperCase();
            register('gst').onChange(e);
          }}
        />

        <div className="flex justify-end space-x-3 pt-4">
          <Button 
            variant="secondary" 
            type="button" 
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            type="submit"
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            Update
          </Button>
        </div>
      </form>
    </Modal>
  );
};